'use client';

import Rating from '@/features/primitives/Rating';
import { getProductCategories } from '@/lib/products';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import './ProductDescriptionSection.scss';

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

export default function ProductDescriptionSection({
  canEditProduct,
  discountedPrice,
  discountInfo,
  messages = {},
  price,
  product,
  savingsLabel,
  stock,
}) {
  const bodyRef = useRef(null);
  const headingRef = useRef(null);
  const previousClampedRef = useRef(true);
  const sectionRef = useRef(null);
  const didMountRef = useRef(false);
  const [clamped, setClamped] = useState(true);
  const [showToggle, setShowToggle] = useState(false);
  const categories = getProductCategories(product);
  const description = String(product?.description || '').trim();
  const descriptionParagraphs = description
    .split(/\r?\n\s*\r?\n|\r?\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
  const firstDescriptionParagraph = descriptionParagraphs[0] || '';
  const hasAdditionalParagraphs = descriptionParagraphs.length > 1;
  const productEyebrow = readMessage(messages, 'productEyebrow', 'Product');
  const unavailableLabel = readMessage(messages, 'unavailable', 'Unavailable');
  const couponFallbackLabel = readMessage(messages, 'couponFallback', 'Coupon');
  const priceLabel = readMessage(messages, 'price', 'Price');
  const productNumberLabel = readMessage(
    messages,
    'summary.productNumber',
    'Product number',
  );
  const brandLabel = readMessage(messages, 'summary.brand', 'Brand');
  const descriptionLabel = readMessage(messages, 'description', 'Description');
  const editProductLabel = readMessage(
    messages,
    'actions.editProduct',
    'Edit product',
  );
  const backToShopLabel = readMessage(
    messages,
    'actions.backToShop',
    'Back to shop',
  );

  useEffect(() => {
    previousClampedRef.current = true;
    setClamped(true);
  }, [product?.productNumber, description]);

  useEffect(() => {
    const element = bodyRef.current;

    if (!element || !firstDescriptionParagraph) {
      setShowToggle(false);
      return undefined;
    }

    const measureOverflow = () => {
      element.classList.add('is-clamped');

      window.requestAnimationFrame(() => {
        const isOverflowing = element.scrollHeight > element.clientHeight + 1;
        setShowToggle(isOverflowing || hasAdditionalParagraphs);

        if (!isOverflowing) {
          element.classList.remove('is-clamped');
        }
      });
    };

    measureOverflow();
    window.addEventListener('resize', measureOverflow);

    return () => {
      window.removeEventListener('resize', measureOverflow);
    };
  }, [firstDescriptionParagraph, hasAdditionalParagraphs]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      previousClampedRef.current = clamped;
      return;
    }

    const shouldScrollToDescription =
      previousClampedRef.current === false && clamped;
    previousClampedRef.current = clamped;

    if (!shouldScrollToDescription) {
      return;
    }

    const target = sectionRef.current;

    if (!target) {
      return;
    }

    window.requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      headingRef.current?.focus?.({ preventScroll: true });
    });
  }, [clamped]);

  return (
    <div className="product-detail-panel" ref={sectionRef}>
      <span className="product-detail-panel__eyebrow">{productEyebrow}</span>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="product-detail-panel__title"
      >
        {product?.name}
      </h1>

      <div className="product-detail-panel__supporting">
        <span className={`product-stock product-stock--${stock?.tone}`}>
          {stock?.label}
        </span>
        <Rating
          value={product?.rating}
          numReviews={product?.numReviews}
          messages={messages}
        />
      </div>

      {categories.length > 0 ? (
        <div className="product-detail-panel__taxonomy">
          {categories.map((category) => (
            <span
              key={category}
              className="product-detail-panel__category-badge"
            >
              {category}
            </span>
          ))}
        </div>
      ) : null}

      <div className="product-detail-panel__stat-grid">
        <div className="product-detail-panel__summary-card">
          <span className="product-detail-panel__summary-label">
            {priceLabel}
          </span>
          {discountInfo?.hasDiscount ? (
            <div>
              <strong className="product-detail-panel__price product-detail-panel__price--discounted">
                {discountedPrice || unavailableLabel}
              </strong>
              <div className="product-detail-panel__price-original">
                {price || unavailableLabel}
              </div>
              <div className="product-detail-panel__discount-note d-none">
                {discountInfo?.couponLabel}{' '}
                {discountInfo?.couponCode || couponFallbackLabel} •{' '}
                {savingsLabel}
              </div>
            </div>
          ) : (
            <strong className="product-detail-panel__price">
              {price || unavailableLabel}
            </strong>
          )}
        </div>

        <div className="product-detail-panel__summary-card">
          <span className="product-detail-panel__summary-label">
            {productNumberLabel}
          </span>
          <strong>{product?.productNumber}</strong>
        </div>

        {product?.brand ? (
          <div className="product-detail-panel__summary-card">
            <span className="product-detail-panel__summary-label">
              {brandLabel}
            </span>
            <strong>{product.brand}</strong>
          </div>
        ) : null}
      </div>

      {description ? (
        <div className="product-detail-panel__summary-card gap-0">
          <span className="product-detail-panel__summary-label">
            {descriptionLabel}
          </span>
          <div className="product-detail-panel__description-body">
            {clamped ? (
              <p
                ref={bodyRef}
                className="product-detail-panel__description-paragraph is-clamped"
              >
                {firstDescriptionParagraph}
              </p>
            ) : (
              descriptionParagraphs.map((paragraph, index) => (
                <p
                  key={`${product?.productNumber || 'product'}-description-${index}`}
                  ref={index === 0 ? bodyRef : undefined}
                  className="product-detail-panel__description-paragraph"
                >
                  {paragraph}
                </p>
              ))
            )}
          </div>
          {showToggle && clamped ? (
            <span
              className="product-detail-panel__description-ellipsis"
              aria-hidden="true"
            >
              ...
            </span>
          ) : null}
          {showToggle ? (
            <Button
              variant="link"
              className="product-detail-panel__description-toggle custom-btn-link flush"
              onClick={() => setClamped((current) => !current)}
              aria-expanded={!clamped}
            >
              {clamped
                ? readMessage(messages, 'descriptionReadMore', 'Read more')
                : readMessage(messages, 'descriptionShowLess', 'Show less')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="product-detail-panel__actions">
        {canEditProduct ? (
          <>
            <Link
              href={`/admin/product/${product?.productNumber}/edit`}
              className="app-button app-button--primary"
            >
              {editProductLabel}
            </Link>

            <Link href="/shop" className="app-button app-button--secondary">
              {backToShopLabel}
            </Link>
          </>
        ) : null}
      </div>
    </div>
  );
}
